import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@environments/environment';
import { SESSION_STORAGE_KEYS } from '@core/constants/storage-keys.constants';
import { IDENTITY_AUTH_PATH } from '@core/constants/auth.constants';
import { CsrfService } from '@core/services/csrf.service';
import { LanguageService } from '@core/services/language.service';
import { StorageService } from '@core/services/storage.service';
import { API_BASE_URL, IDENTITY_API_BASE_URL } from '@core/constants/api-tokens.constants';

function isIdentityRequest(url: string, identityApiBaseUrl: string): boolean {
  return url.startsWith(identityApiBaseUrl);
}

function isApiRequest(url: string, apiBaseUrl: string, identityApiBaseUrl: string): boolean {
  return (
    url.startsWith('/api/') ||
    url.startsWith(apiBaseUrl) ||
    isIdentityRequest(url, identityApiBaseUrl)
  );
}

/**
 * Identity's `auth/…` endpoints (CSRF, login intent, logout). A CRM path that merely contains
 * `/auth/` is not one.
 */
function isAuthEndpoint(url: string, identityApiBaseUrl: string): boolean {
  return (
    url.startsWith('/api/auth/') || url.startsWith(`${identityApiBaseUrl}${IDENTITY_AUTH_PATH}`)
  );
}

/**
 * Interceptor: all API requests use cookies (no Bearer token).
 * Adds X-Tenant-Id once the session has stored one (Identity `me` itself goes without: the tenant
 * comes from the session cookie); Accept-Language en-US|ar-SA from {@link LanguageService};
 * X-XSRF-TOKEN from {@link CsrfService} — the cookie session travels cross-site, so the
 * antiforgery token is what proves the request came from this app;
 * X-Brooch-Application on Identity calls so Identity resolves CRM roles/permissions;
 * X-Brooch-Dev-Client on local `ng serve` only (see {@link environment.sendDevClientHeader});
 * always sends withCredentials so cookies are sent.
 *
 * The CSRF read is synchronous by design: an interceptor cannot await a fetch without stalling
 * every request. Before the first token arrives the header is simply omitted.
 */
export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const storage = inject(StorageService);
  const languageService = inject(LanguageService);
  const csrf = inject(CsrfService);
  const apiBaseUrl = inject(API_BASE_URL);
  const identityApiBaseUrl = inject(IDENTITY_API_BASE_URL);
  const tenantId = storage.getSessionItem(SESSION_STORAGE_KEYS.tenantId);

  const isApi = isApiRequest(req.url, apiBaseUrl, identityApiBaseUrl);
  const shouldAttachTenant =
    Boolean(tenantId) && isApi && !isAuthEndpoint(req.url, identityApiBaseUrl);
  const csrfToken = isApi ? csrf.current() : null;

  const setHeaders: Record<string, string> = {};
  if (shouldAttachTenant) {
    setHeaders['X-Tenant-Id'] = tenantId!;
  }
  if (isApi) {
    setHeaders['Accept-Language'] = languageService.language() === 'ar' ? 'ar-SA' : 'en-US';
  }
  if (csrfToken) {
    setHeaders['X-XSRF-TOKEN'] = csrfToken;
  }
  if (isIdentityRequest(req.url, identityApiBaseUrl)) {
    setHeaders['X-Brooch-Application'] = environment.applicationKey;
  }
  if (isApi && environment.sendDevClientHeader) {
    setHeaders['X-Brooch-Dev-Client'] = 'true';
  }

  req = req.clone({
    withCredentials: true,
    ...(Object.keys(setHeaders).length > 0 ? { setHeaders } : {}),
  });

  return next(req);
};
